/**
 * D5 (docs/plans/departments.md): the rules follow the task. In department mode the department a piece of work
 * runs under — its rate, billable default, whether it's clocked, GPS, whether it feeds the books, which row of a
 * report it lands in — is its TASK's (subtasks follow their top-level task; no department = General). Decided per
 * time entry (Lj 2026-10-04): one clock session can hold Field and Admin work, each entry carries its own.
 * Off — or for time with no task — it is the project's department, exactly as before. Pure — no I/O.
 */
import { departmentOf, listDepartments, withDepartmentDefaults, GENERAL_DEPARTMENT_ID } from './hierarchy.js';
import { taskDepartmentResolver } from './taskdepartments.js';

/**
 * Lookups for "which department does this work run under". Build once per list.
 * → { on, forTask(task | id), forProject(id), forEntry(entry, task), forWork({ taskId, projectId }) } — each a
 *   department doc with every setting filled in (withDepartmentDefaults).
 */
export function workDepartments({ tasks = [], projects = [], departments = [], departmentMode = false } = {}) {
  const byId = new Map(listDepartments(departments).map((d) => [d.id, d]));
  const general = byId.get(GENERAL_DEPARTMENT_ID) || withDepartmentDefaults({ id: GENERAL_DEPARTMENT_ID });
  const taskById = new Map((tasks || []).filter((t) => t && t.id).map((t) => [t.id, t]));
  const projById = new Map((projects || []).filter((p) => p && p.id).map((p) => [p.id, p]));
  const ofTask = departmentMode ? taskDepartmentResolver(tasks, departments, projects) : null;
  const forProject = (pid) => departmentOf(pid ? projById.get(pid) || null : null, departments);
  const forTask = (taskOrId) => {
    const t = typeof taskOrId === 'string' ? taskById.get(taskOrId) : taskOrId;
    if (!t) return null;
    if (ofTask) return byId.get(ofTask(t)) || general;
    return forProject(t.projectId);
  };
  // An entry banked in department mode carries the department it ran under (kept if the task moves later).
  const forEntry = (entry, task) => (ofTask && entry?.departmentId && byId.get(entry.departmentId)) || forTask(task) || general;
  const forWork = ({ taskId = null, projectId = null } = {}) => (taskId && forTask(taskId)) || forProject(projectId);
  return { on: !!departmentMode, forTask, forProject, forEntry, forWork };
}

/**
 * The clock session stamp. Department mode: `perTask` (the books bridge and reports take each entry's own
 * department) plus the department of the task the session started on, for show. Off: the project's department,
 * `feedBooks: false` when it keeps its time out of the books — as before.
 */
export function sessionDepartmentStamp(ctx, projectId, taskId = null) {
  const w = workDepartments({ tasks: ctx.tasks, projects: ctx.projects, departments: ctx.departments, departmentMode: ctx.departmentMode });
  if (w.on) return { departmentId: taskId ? w.forTask(taskId)?.id || null : null, perTask: true };
  const d = w.forProject(projectId);
  return { departmentId: d.id, ...(d.feedBooks === false ? { feedBooks: false } : {}) };
}

/** A time entry stamped with the department its task runs under (department mode only; unchanged when off). */
export function stampEntryDepartment(entry, task, ctx) {
  if (!entry || !ctx?.departmentMode || !task) return entry;
  const d = workDepartments({ tasks: ctx.tasks, projects: ctx.projects, departments: ctx.departments, departmentMode: true }).forTask(task);
  return d ? { ...entry, departmentId: d.id } : entry;
}

/**
 * The department a project's setup / cleanup checklist tasks go in (department mode): its project type's
 * department, else the project's own (setup and cleanup are on-site duties — Field for a job). Null = General.
 */
export const bucketDepartmentId = (project, type) => type?.departmentId || project?.departmentId || null;
